import { useCallback, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { DndContext, DragOverlay, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent, type DragStartEvent } from '@dnd-kit/core';
import type { OrcamentoResponse } from '@triade/contracts';
import { useCondicoesPagamento } from '@/api/condicoes-pagamento';
import { usePode } from '@/api/me';
import { LegendaAlertaCusto } from './alerta-custo';
import { useAprovarVersao, useEnviarVersao, useMudarSituacaoOrcamento, useOrcamentos, useResumoOrcamentos } from '@/api/orcamentos';
import { useTabelasPreco } from '@/api/tabelas-preco';
import { BarraDeAcoesLista } from '@/components/barra-lista';
import { DialogoSituacao, type DadosSituacao } from '@/components/orcamento/dialogo-situacao';
import { DialogoAcaoVersao, type AcaoVersao } from '@/components/orcamento/versoes';
import { ROTULO_STATUS } from '@/components/orcamento/tipos';
import { useSessao } from '@/lib/session';
import { BotaoRelatorios } from '@/relatorios';
import { X } from 'lucide-react';
import { AlternarVisao } from './alternar-visao';
import { buscaComPeriodo, DIAS_PERIODO_FUNIL, gravarPeriodoLembrado, lerPeriodoLembrado } from './funil-periodo';
import { CartaoFlutuante } from './cartao-kanban';
import { ColunaKanban } from './coluna-kanban';
import { FiltrosLista } from './filtros-lista';
import { paraRelatorio, paraResumo, STATUS_ORDEM, TRANSICOES, type BuscaLista, type PeriodoFunil, type StatusOrcamento } from './tipos';
import { ErroFormacao } from '@/components/formacao/avisos-formacao';

/** Cartoes por coluna: os mais recentes; o resto fica na lista ("ver todos"). */
const POR_COLUNA = 20;

/** Tela "Orçamentos — kanban" (/vender/orcamentos/kanban): uma coluna por situação da API (docs/design/Pipeline.dc.html). */
export function KanbanPage({ busca }: { busca: BuscaLista }) {
  const { empresaId } = useSessao();
  const navigate = useNavigate();
  // Periodo do funil (S36-D5): URL `periodo`, ou a escolha lembrada do usuario (padrao: criados nos ultimos 60 dias).
  const periodo = busca.periodo ?? lerPeriodoLembrado();
  const buscaEfetiva = buscaComPeriodo(busca, periodo);
  const resumo = useResumoOrcamentos(paraResumo(buscaEfetiva));
  const tabelas = useTabelasPreco({ limit: 200 });
  const condicoes = useCondicoesPagamento({ limit: 200 });
  const filtros = paraResumo(buscaEfetiva);
  const rascunho = useOrcamentos({ ...filtros, status: 'RASCUNHO', limit: POR_COLUNA }, { manter: true });
  const enviado = useOrcamentos({ ...filtros, status: 'ENVIADO', limit: POR_COLUNA }, { manter: true });
  const negociacao = useOrcamentos({ ...filtros, status: 'EM_NEGOCIACAO', limit: POR_COLUNA }, { manter: true });
  const aprovado = useOrcamentos({ ...filtros, status: 'APROVADO', limit: POR_COLUNA }, { manter: true });
  const perdido = useOrcamentos({ ...filtros, status: 'PERDIDO', limit: POR_COLUNA }, { manter: true });
  const consultas = { RASCUNHO: rascunho, ENVIADO: enviado, EM_NEGOCIACAO: negociacao, APROVADO: aprovado, PERDIDO: perdido };

  const mudarSituacao = useMudarSituacaoOrcamento();
  // Enviado e Aprovado saem pelas rotas de versao (congelar a versao e aprovar UMA versao), nunca por POST :id/situacao.
  const enviarVersao = useEnviarVersao();
  const aprovarVersao = useAprovarVersao();
  const podeEditar = usePode().pode('orcamento.editar');

  const [arrastando, setArrastando] = useState<OrcamentoResponse | null>(null);
  const [pendente, setPendente] = useState<{ orcamento: OrcamentoResponse; para: StatusOrcamento } | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const sensores = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }), useSensor(KeyboardSensor));
  const mudar = useCallback(
    (mudanca: Partial<BuscaLista>) => void navigate({ to: '/vender/orcamentos/kanban', search: (anterior: BuscaLista) => ({ ...anterior, ...mudanca }), replace: true }),
    [navigate],
  );
  /** Troca o periodo do funil: vale na URL e fica lembrado para a proxima visita. */
  const trocarPeriodo = useCallback(
    (novo: PeriodoFunil) => {
      gravarPeriodoLembrado(novo);
      mudar({ periodo: novo });
    },
    [mudar],
  );
  const nomeTabela = useCallback((id: string | null) => (id ? tabelas.data?.find((t) => t.id === id)?.nome : undefined), [tabelas.data]);
  const nomeCondicao = useCallback((id: string) => condicoes.data?.find((c) => c.id === id)?.nome, [condicoes.data]);

  const pedirMover = useCallback((o: OrcamentoResponse, para: StatusOrcamento) => {
    if (!podeEditar || !TRANSICOES[o.status].includes(para)) return; // so o que a API aceita, e so com orcamento.editar
    setOk(null);
    setPendente({ orcamento: o, para });
  }, [podeEditar]);

  function aoSoltar(e: DragEndEvent) {
    const o = arrastando;
    setArrastando(null);
    const alvo = e.over?.id as StatusOrcamento | undefined;
    if (o && alvo && alvo !== o.status) pedirMover(o, alvo);
  }

  /**
   * Chama a API. PERDIDO e EM_NEGOCIACAO: POST :id/situacao (PERDIDO leva motivo/categoria). ENVIADO e APROVADO: a versao
   * de referencia do cartao (enviar ou aprovar essa versao). Erro: o dialogo fica aberto e o mostra.
   */
  async function confirmar(dados: DadosSituacao) {
    if (!pendente) return;
    const { orcamento, para } = pendente;
    if (para === 'ENVIADO') await enviarVersao.mutateAsync({ id: orcamento.id, n: orcamento.versaoNumero });
    else if (para === 'APROVADO') await aprovarVersao.mutateAsync({ id: orcamento.id, n: orcamento.versaoNumero });
    else await mudarSituacao.mutateAsync({ id: orcamento.id, para, ...dados });
    setPendente(null);
    setOk(`Orçamento ${orcamento.numeroExibido} agora está ${ROTULO_STATUS[para].toLowerCase()}.`);
  }

  const ocupado = mudarSituacao.isPending || enviarVersao.isPending || aprovarVersao.isPending;
  const acaoVersao: AcaoVersao | null = pendente?.para === 'ENVIADO' ? 'enviar' : pendente?.para === 'APROVADO' ? 'aprovar' : null;

  return (
    <div className="mx-auto flex max-w-[1376px] flex-col gap-[18px] pb-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-small text-ink-muted">
            Vender <span aria-hidden="true">/</span> Orçamentos
          </div>
          <h1 className="mt-1 text-display font-semibold tracking-[-0.01em]">Funil de orçamentos</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <AlternarVisao atual="kanban" busca={buscaEfetiva} />
        </div>
      </div>

      <section aria-label="Filtros do funil" className="rounded-2xl border border-border bg-card">
        <div className="flex flex-wrap items-center gap-2 px-4 pt-3 text-small" data-testid="periodo-funil">
          {periodo === 'ULTIMOS_60' ? (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-line-soft py-1 pl-3 pr-1.5 text-ink-body">
              Criados nos últimos {DIAS_PERIODO_FUNIL} dias
              <button
                type="button"
                aria-label="Remover o filtro de período (mostrar todos os criados)"
                onClick={() => trocarPeriodo('TODOS')}
                className="inline-flex size-5 items-center justify-center rounded-full hover:bg-card"
              >
                <X aria-hidden="true" className="size-3.5" />
              </button>
            </span>
          ) : (
            <>
              <span className="text-ink-secondary">Criados: todos os períodos</span>
              <button type="button" onClick={() => trocarPeriodo('ULTIMOS_60')} className="font-medium text-primary hover:underline">
                Filtrar os criados nos últimos {DIAS_PERIODO_FUNIL} dias
              </button>
            </>
          )}
        </div>
        <FiltrosLista busca={busca} onBusca={mudar} comAbas={false} />
      </section>

      {empresaId === null && <p className="rounded-2xl border border-dashed border-border bg-card px-5 py-10 text-center text-small text-ink-muted">Escolha a empresa no menu lateral para ver os orçamentos.</p>}
      {resumo.isError && !resumo.data && (
        <ErroFormacao erro={resumo.error} onTentar={() => void resumo.refetch()} titulo="Não foi possível carregar as contagens" />
      )}
      {ok && (
        <div role="status" data-testid="ok-mover" className="rounded-xl border border-border bg-line-soft px-4 py-3 text-small text-ink-body">
          {ok}
        </div>
      )}

      <DndContext
        sensors={sensores}
        onDragStart={(e: DragStartEvent) => setArrastando((e.active.data.current?.orcamento as OrcamentoResponse | undefined) ?? null)}
        onDragEnd={aoSoltar}
        onDragCancel={() => setArrastando(null)}
      >
        <div className="overflow-x-auto pb-2" aria-busy={ocupado}>
          <div className="grid min-w-[1100px] grid-cols-5 gap-3.5">
            {STATUS_ORDEM.map((s) => {
              const c = consultas[s];
              return (
                <ColunaKanban
                  key={s}
                  status={s}
                  resumo={resumo.data?.porStatus[s]}
                  orcamentos={c.data?.itens}
                  total={c.data?.total}
                  carregando={c.isPending}
                  erro={c.isError && !c.data ? c.error : null}
                  onTentar={() => void c.refetch()}
                  arrastando={arrastando?.status ?? null}
                  aceita={podeEditar && arrastando !== null && TRANSICOES[arrastando.status].includes(s)}
                  busca={buscaEfetiva}
                  nomeTabela={nomeTabela}
                  nomeCondicao={nomeCondicao}
                  onMover={pedirMover}
                  podeMover={podeEditar}
                />
              );
            })}
          </div>
        </div>
        <DragOverlay>{arrastando ? <CartaoFlutuante orcamento={arrastando} nomeTabela={nomeTabela} nomeCondicao={nomeCondicao} /> : null}</DragOverlay>
      </DndContext>

      <p className="text-caption text-ink-muted">
        Fluxo: rascunho → enviado (ou perdido); enviado → em negociação, aprovado ou perdido; em negociação → aprovado ou perdido. Aprovado e perdido são finais;
        perdido pede o motivo. Arraste pela alça ou use o menu do cartão.
      </p>
      <LegendaAlertaCusto />

      <DialogoSituacao
        numero={pendente?.orcamento.numero ?? 0}
        para={pendente && !acaoVersao ? pendente.para : null}
        onConfirmar={confirmar}
        onCancelar={() => setPendente(null)}
      />
      <DialogoAcaoVersao
        key={acaoVersao ?? 'fechado'}
        acao={acaoVersao}
        numeroExibido={pendente?.orcamento.numeroExibido ?? ''}
        onConfirmar={() => confirmar({})}
        onCancelar={() => setPendente(null)}
      />
      <BarraDeAcoesLista
        novo={podeEditar ? { rotulo: 'Novo orçamento', onClick: () => void navigate({ to: '/vender/orcamentos/novo' }) } : undefined}
        totalSemPaginar={resumo.data?.geral.quantidade}
        relatorios={<BotaoRelatorios tela="orcamentos-lista" params={paraRelatorio(buscaEfetiva)} />}
      />
    </div>
  );
}
